import React, { useState } from "react";
import { Heart, MessageCircle } from "lucide-react";
import { TspBadge, TspFormField, TspModal } from "../../../components/primitives";
import { formatDate } from "../../../utils/tspFormat";
import { COMMENT_MAX, paragraphsOf, readMinutes, validateComment } from "../storyModel";

// The reader: full story, like button and comments. Liking and commenting need
// `canParticipate` (contributor access); everyone with access to the page can
// read and see the counts.
export default function StoryDetailModal({ story, category, canParticipate, onClose, onLike, onComment }) {
  const [text, setText] = useState("");
  const [error, setError] = useState("");

  const submit = (event) => {
    event.preventDefault();
    const found = validateComment(text);
    setError(found);
    if (found) {
      document.getElementById("tsp-story-comment")?.focus();
      return;
    }
    onComment(story.id, text);
    setText("");
  };

  return (
    <TspModal open title={story.title} size="lg" onClose={onClose}>
      <article className="tsp-story-reader">
        {story.imageUrl && <img className="tsp-story-reader-image" src={story.imageUrl} alt="" />}

        <div className="tsp-story-reader-byline">
          {category && <TspBadge tone={category.tone}>{category.label}</TspBadge>}
          <span>
            By <strong>{story.author}</strong> · {formatDate(story.createdAt)} · {readMinutes(story.content)} min read
          </span>
        </div>

        <div className="tsp-story-reader-text">
          {paragraphsOf(story.content).map((paragraph, index) => (
            <p key={index}>{paragraph}</p>
          ))}
        </div>

        <div className="tsp-story-reader-actions">
          <button
            type="button"
            className={`tsp-like-btn ${story.likedByMe ? "liked" : ""}`}
            aria-pressed={story.likedByMe}
            disabled={!canParticipate}
            title={canParticipate ? undefined : "Liking needs contributor access"}
            onClick={() => onLike(story.id)}
          >
            <Heart size={15} aria-hidden="true" fill={story.likedByMe ? "currentColor" : "none"} /> {story.likedByMe ? "Liked" : "Like"} · {story.likes}
          </button>
          <span className="tsp-muted-text">
            <MessageCircle size={14} aria-hidden="true" /> {story.comments.length} {story.comments.length === 1 ? "comment" : "comments"}
          </span>
        </div>

        <section className="tsp-story-comments" aria-label="Comments">
          <h4>Comments</h4>
          {story.comments.length === 0 ? (
            <p className="tsp-muted-text">No comments yet.</p>
          ) : (
            <ul>
              {story.comments.map((entry) => (
                <li key={entry.id}>
                  <strong>{entry.author}</strong> <span className="tsp-muted-text">· {formatDate(entry.createdAt)}</span>
                  <p>{entry.text}</p>
                </li>
              ))}
            </ul>
          )}

          {canParticipate ? (
            <form onSubmit={submit} noValidate className="tsp-story-comment-form">
              <TspFormField id="tsp-story-comment" label="Add a comment" error={error} hint={`${text.length}/${COMMENT_MAX}`}>
                <textarea
                  id="tsp-story-comment"
                  className="tsp-field-control"
                  rows={3}
                  value={text}
                  aria-invalid={error ? "true" : undefined}
                  aria-describedby={error ? "tsp-story-comment-error" : "tsp-story-comment-hint"}
                  onChange={(event) => {
                    setText(event.target.value);
                    setError("");
                  }}
                />
              </TspFormField>
              <button type="submit" className="tsp-btn tsp-btn-primary">
                Post Comment
              </button>
            </form>
          ) : (
            <p className="tsp-muted-text">Commenting needs contributor access.</p>
          )}
        </section>
      </article>
    </TspModal>
  );
}
